import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  IconButton,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import axios from 'axios';
import PokemonSelector from '../components/teambuilder/PokemonSelector';
import TeamSuggestions from '../components/teambuilder/TeamSuggestions';
import PokemonSprite from '../components/PokemonSprite';
import SEO from '../components/SEO';
import { formatGameFormat } from '../utils/formatHelpers';

/**
 * Team Builder Page
 * Dos modos:
 * - < 6 Pokémon: Sugerir Pokémon para completar equipo
 * - ≥ 6 Pokémon: Optimizar y encontrar mejor combinación de 6
 */
const TeamBuilderPage = () => {
  const theme = useTheme();

  // Estados principales
  const [selectedFormat, setSelectedFormat] = useState('all');
  const [formats, setFormats] = useState([]);
  const [isLoadingFormats, setIsLoadingFormats] = useState(true);

  const [availablePokemon, setAvailablePokemon] = useState([]);
  const [isLoadingPokemon, setIsLoadingPokemon] = useState(false);

  const [selectedPokemon, setSelectedPokemon] = useState([]);
  const [lockedPokemon, setLockedPokemon] = useState(new Set());
  const [suggestions, setSuggestions] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  // Determinar modo según cantidad de Pokémon
  const mode = selectedPokemon.length >= 6 ? 'optimize' : 'suggest';

  // Cargar formatos disponibles
  useEffect(() => {
    const fetchFormats = async () => {
      try {
        setIsLoadingFormats(true);
        const response = await axios.get('/api/games/formats');
        
        if (response.data && response.data.formats) {
          // Filtrar formatos válidos
          const validFormats = response.data.formats
            .sort((a, b) => b.localeCompare(a));
          
          setFormats(validFormats);
          
          // No cambiamos el default ('all'), que ya está puesto en useState
        }
      } catch (err) {
        console.error('Error fetching formats:', err);
        setError('Failed to load formats. Please refresh the page.');
      } finally {
        setIsLoadingFormats(false);
      }
    };

    fetchFormats();
  }, []);

  // Cargar Pokémon disponibles cuando cambia el formato
  useEffect(() => {
    const fetchAvailablePokemon = async () => {
      try {
        setIsLoadingPokemon(true);
        setError(null);

        console.log('📡 Fetching available Pokémon for format:', selectedFormat);

        const params = selectedFormat !== 'all' ? { format: selectedFormat } : {};
        const response = await axios.get('/api/team-builder/available-pokemon', { params });

        if (response.data && response.data.pokemon) {
          setAvailablePokemon(response.data.pokemon);
        }
      } catch (err) {
        console.error('Error fetching available Pokémon:', err);
        setError('Failed to load available Pokémon for this format.');
      } finally {
        setIsLoadingPokemon(false);
      }
    };

    fetchAvailablePokemon();
  }, [selectedFormat]);

  // Manejar cambio de formato
  const handleFormatChange = (event) => {
    const newFormat = event.target.value;
    setSelectedFormat(newFormat);
    setSelectedPokemon([]);
    setLockedPokemon(new Set());
    setSuggestions([]);
    setActiveStep(0);
  };

  // Manejar selección de Pokémon
  const handleSelectionChange = (newSelection) => {
    setSelectedPokemon(newSelection);
    setSuggestions([]); // Limpiar sugerencias al cambiar selección
  };

  // Remover un Pokémon de la selección
  const handleRemovePokemon = (pokemonId) => {
    setSelectedPokemon(prev => prev.filter(p => p.id !== pokemonId));
    setLockedPokemon(prev => {
      const next = new Set(prev);
      next.delete(pokemonId);
      return next;
    });
    setSuggestions([]);
  };

  // Toggle lock de un Pokémon
  const handleToggleLock = (pokemonId) => {
    setLockedPokemon(prev => {
      const next = new Set(prev);
      if (next.has(pokemonId)) {
        next.delete(pokemonId);
      } else {
        next.add(pokemonId);
      }
      return next;
    });
  };

  // Limpiar selección completa
  const handleClearSelection = () => {
    setSelectedPokemon([]);
    setLockedPokemon(new Set());
    setSuggestions([]);
  };

  // Analizar equipo (sugerir o optimizar)
  const handleAnalyze = async () => {
    if (selectedPokemon.length === 0) {
      setError('Please select at least one Pokémon');
      return;
    }

    try {
      setIsAnalyzing(true);
      setError(null);
      setSuggestions([]);

      if (mode === 'suggest') {
        // Modo: Sugerir Pokémon para completar equipo
        console.log('💡 Requesting suggestions...');
        
        const response = await axios.post('/api/team-builder/suggest-pokemon', {
          team: selectedPokemon,
          format: selectedFormat !== 'all' ? selectedFormat : null,
          limit: 60
        });

        if (response.data && response.data.suggestions) {
          setSuggestions(response.data.suggestions);
          setActiveStep(2);
        }
      } else {
        // Modo: Optimizar combinación de 6
        console.log('🔍 Optimizing team combinations...');
        
        const response = await axios.post('/api/team-builder/optimize-team', {
          pokemon: selectedPokemon,
          format: selectedFormat !== 'all' ? selectedFormat : null,
          limit: 5,
          lockedPokemon: Array.from(lockedPokemon)
        });

        if (response.data && response.data.combinations) {
          setSuggestions(response.data.combinations);
          setActiveStep(2);
        }
      }
    } catch (err) {
      console.error('Error analyzing team:', err);
      setError(
        err.response?.data?.error || 
        'Failed to analyze team. Please try again.'
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  const steps = ['Select Format', 'Choose Pokémon', 'View Results'];

  const getTypeColor = (typeName) => {
    const typeColors = {
      normal: '#A8A878', fire: '#F08030', water: '#6890F0',
      electric: '#F8D030', grass: '#78C850', ice: '#98D8D8',
      fighting: '#C03028', poison: '#A040A0', ground: '#E0C068',
      flying: '#A890F0', psychic: '#F85888', bug: '#A8B820',
      rock: '#B8A038', ghost: '#705898', dragon: '#7038F8',
      dark: '#705848', steel: '#B8B8D0', fairy: '#EE99AC'
    };
    return typeColors[typeName?.toLowerCase()] || '#777';
  };

  return (
    <Container maxWidth="xl">
      <SEO 
        title="Pokémon Team Builder | Trainer Academy” VGC Synergy & Type Coverage Analyzer"
        description="Build and analyze your competitive PokÃ©mon VGC team. Get automatic team suggestions, check type coverage, identify weaknesses, and find the perfect team synergy with real usage stats."
        keywords="pokemon team builder, vgc team builder, pokemon synergy, pokemon type coverage, competitive pokemon team, team suggestions vgc, pokemon weaknness checker, pokemon teambuilder"
      />
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              mb: 1
            }}
          >
            Team Builder
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Build and optimize your competitive Pokémon team
          </Typography>
        </Box>

        {/* Stepper */}
        <Paper 
          elevation={3}
          sx={{ 
            p: 3, 
            mb: 4, 
            backgroundColor: 'rgba(30, 30, 30, 0.9)' 
          }}
        >
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      color: 'rgba(255,255,255,0.3)',
                      '&.Mui-active': { color: theme.palette.primary.main },
                      '&.Mui-completed': { color: theme.palette.primary.main }
                    }
                  }}
                  sx={{
                    '& .MuiStepLabel-label': {
                      color: 'rgba(255,255,255,0.6)',
                      '&.Mui-active': { color: 'white' },
                      '&.Mui-completed': { color: 'white' }
                    }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Panel izquierdo: Selección */}
          <Grid item xs={12} lg={8}>
            <Paper 
              elevation={3}
              sx={{ 
                p: 3, 
                backgroundColor: 'rgba(30, 30, 30, 0.9)',
                minHeight: 400
              }}
            >
              {/* Contenido combinado: Selector de formato y de Pokémon */}
              {selectedFormat ? (
                <PokemonSelector
                  availablePokemon={availablePokemon}
                  selectedPokemon={selectedPokemon}
                  onSelectionChange={handleSelectionChange}
                  loading={isLoadingPokemon}
                  maxSelection={null} // Sin límite
                  formatSelector={
                    <FormControl fullWidth>
                      <InputLabel 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.7)',
                          '&.Mui-focused': { color: theme.palette.primary.main }
                        }}
                      >
                        Select Format
                      </InputLabel>
                      <Select
                        value={selectedFormat}
                        onChange={handleFormatChange}
                        label="Select Format"
                        disabled={isLoadingFormats}
                        sx={{
                          color: 'white',
                          '.MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.2)'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main
                          },
                          '.MuiSvgIcon-root': { color: 'white' }
                        }}
                      >
                        <MenuItem value="all">All Pokémon</MenuItem>
                        {formats.map((format) => (
                          <MenuItem key={format} value={format}>
                            {formatGameFormat(format)}
                          </MenuItem>
                        ))}
                      </Select>
                      {!isLoadingPokemon && availablePokemon.length > 0 && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.5, display: 'block', textAlign: 'right' }}>
                          {availablePokemon.length} Pokémon available
                        </Typography>
                      )}
                    </FormControl>
                  }
                />
              ) : (
                <Box>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-focused': { color: theme.palette.primary.main }
                      }}
                    >
                      Select Format / Regulation
                    </InputLabel>
                    <Select
                      value={selectedFormat}
                      onChange={handleFormatChange}
                      label="Select Format / Regulation"
                      disabled={isLoadingFormats}
                      sx={{
                        color: 'white',
                        '.MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main
                        },
                        '.MuiSvgIcon-root': { color: 'white' }
                      }}
                    >
                      {formats.map((format) => (
                        <MenuItem key={format} value={format}>
                          {formatGameFormat(format)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Alert severity="info">
                    Please select a format to see available Pokémon
                  </Alert>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Panel derecho: Equipo actual y acciones */}
          <Grid item xs={12} lg={4}>
            <Paper 
              elevation={3}
              sx={{ 
                p: 3, 
                backgroundColor: 'rgba(30, 30, 30, 0.9)',
                position: 'sticky',
                top: 20
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
                Current Team
              </Typography>

              {/* Info de modo */}
              <Alert 
                severity={mode === 'suggest' ? 'info' : 'success'}
                sx={{ mb: 2 }}
              >
                {mode === 'suggest' ? (
                  <>
                    <strong>Suggestion Mode</strong>
                    <br />
                    Select 1-5 Pokémon to get suggestions or select 7 or more to optimize your team
                  </>
                ) : (
                  <>
                    <strong>Optimization Mode</strong>
                    <br />
                    Finding best combination of 6 from your selection.
                    {lockedPokemon.size > 0
                      ? ` ${lockedPokemon.size} Pokémon locked.`
                      : ' Lock Pokémon to always include them.'}
                  </>
                )}
              </Alert>

              {/* Counter */}
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                  {selectedPokemon.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Pokémon Selected
                </Typography>
              </Box>

              {/* Lista de Pokémon seleccionados */}
              {selectedPokemon.length > 0 ? (
                <Box sx={{ mb: 2, maxHeight: 400, overflowY: 'auto' }}>
                  {selectedPokemon.map((pokemon) => {
                    const isLocked = lockedPokemon.has(pokemon.id);
                    return (
                      <Card
                        key={pokemon.id}
                        sx={{
                          mb: 1,
                          backgroundColor: isLocked ? 'rgba(255, 200, 0, 0.08)' : 'rgba(50, 50, 50, 0.5)',
                          border: isLocked ? '1px solid rgba(255, 200, 0, 0.4)' : '1px solid transparent',
                          display: 'flex',
                          alignItems: 'center',
                          p: 1
                        }}
                      >
                        <Box sx={{ width: 50, height: 50, mr: 1, flexShrink: 0 }}>
                          <PokemonSprite
                            pokemon={pokemon}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {pokemon.displayName}
                          </Typography>
                          {pokemon.types && (
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                              {pokemon.types.map((typeObj) => (
                                <Chip
                                  key={typeObj.name}
                                  label={typeObj.name}
                                  size="small"
                                  sx={{
                                    backgroundColor: getTypeColor(typeObj.name),
                                    color: 'white',
                                    fontSize: '0.65rem',
                                    height: 18
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                        {mode === 'optimize' && (
                          <IconButton
                            size="small"
                            onClick={() => handleToggleLock(pokemon.id)}
                            title={isLocked ? 'Unlock Pokémon' : 'Lock Pokémon'}
                            sx={{ color: isLocked ? '#FFC800' : 'rgba(255,255,255,0.3)' }}
                          >
                            {isLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleRemovePokemon(pokemon.id)}
                          sx={{ color: 'rgba(255,255,255,0.5)' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Card>
                    );
                  })}
                </Box>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No Pokémon selected yet
                </Alert>
              )}

              {/* Botones de acción */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={isAnalyzing ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                  onClick={handleAnalyze}
                  disabled={selectedPokemon.length === 0 || isAnalyzing}
                >
                  {isAnalyzing ? 'Analyzing...' : mode === 'suggest' ? 'Get Suggestions' : 'Optimize Team'}
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleClearSelection}
                  disabled={selectedPokemon.length === 0}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'rgba(255,255,255,0.5)',
                      backgroundColor: 'rgba(255,255,255,0.05)'
                    }
                  }}
                >
                  Clear Selection
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Resultados */}
        {suggestions.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Paper 
              elevation={3}
              sx={{ 
                p: 3, 
                backgroundColor: 'rgba(30, 30, 30, 0.9)' 
              }}
            >
              <TeamSuggestions
                suggestions={suggestions}
                mode={mode}
                loading={isAnalyzing}
                onAddPokemon={(pokemon) => {
                  if (selectedPokemon.length < 6) {
                    const newTeam = [...selectedPokemon, pokemon];
                    setSelectedPokemon(newTeam);
                    // Si llegamos a 6 se queda ahí o podemos ejecutar de nuevo, 
                    // pero al añadir un sexto daría error el suggest-pokemon.
                    // Si tiene < 6, pedimos nuevas sugerencias para el equipo actualizado:
                    if (newTeam.length < 6) {
                      setIsAnalyzing(true);
                      axios.post('/api/team-builder/suggest-pokemon', {
                        team: newTeam,
                        format: selectedFormat !== 'all' ? selectedFormat : null,
                        limit: 60
                      })
                      .then(response => {
                        if (response.data && response.data.suggestions) {
                          setSuggestions(response.data.suggestions);
                        }
                      })
                      .catch(err => {
                        console.error('Error fetching new suggestions:', err);
                      })
                      .finally(() => {
                        setIsAnalyzing(false);
                      });
                    } else {
                      setSuggestions([]); // Ya no podemos sugerir más
                    }
                  }
                }}
              />
            </Paper>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default TeamBuilderPage;
